import { BRAND } from '@onuw/roles-data'
import { useEffect, useState } from 'react'
import { ADS_ENABLED } from '../../lib/config'

/** What the TCF API (`__tcfapi`, set up by Google's consent dialog) reports; only the fields read here. */
interface TcData {
  gdprApplies?: boolean
  listenerId?: number
}

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
    adsbygoogle?: unknown[]
    googlefc?: {
      callbackQueue?: unknown[]
      showRevocationMessage?: () => void
    }
    __tcfapi?: (
      command: string,
      version: number,
      callback: (data: TcData | null, success: boolean) => void,
      parameter?: unknown,
    ) => void
  }
}

/**
 * Where Google's consent dialog (Privacy & messaging's European regulations message) asks before any cookie: the
 * EU, with Åland and the outermost regions that have country codes of their own, then Iceland, Liechtenstein,
 * Norway, the UK and Switzerland.
 */
const ASK_FIRST = (
  'AT BE BG CY CZ DE DK EE ES FI FR GR HR HU IE IT LT LU LV MT NL PL PT RO SE SI SK ' +
  'AX GF GP MF MQ RE YT IS LI NO GB CH'
).split(' ')

/** Re-opens Google's consent dialog; offered only where the dialog applies (`usePrivacySettings`). */
export const openPrivacySettings = () => window.googlefc?.showRevocationMessage?.()

/**
 * Whether to offer "Privacy settings": only to visitors Google's consent dialog applies to, once its API is ready.
 * Anywhere else Google never shows the dialog and has nothing to re-open.
 */
export function usePrivacySettings(): boolean {
  const [show, setShow] = useState(false)
  useEffect(() => {
    if (!ADS_ENABLED) return
    let live = true
    // Google's queue works before its script has loaded, and runs at once after
    window.googlefc ??= {}
    const fc = window.googlefc
    fc.callbackQueue ??= []
    fc.callbackQueue.push({
      CONSENT_API_READY: () =>
        window.__tcfapi?.('addEventListener', 2, (data, success) => {
          if (live) setShow(success && data?.gdprApplies === true)
          if (data?.listenerId !== undefined)
            window.__tcfapi?.('removeEventListener', 2, () => {}, data.listenerId)
        }),
    })
    return () => {
      live = false
    }
  }, [])
  return show
}

const loadScript = (src: string, attrs: Record<string, string> = {}) => {
  if (document.querySelector(`script[src="${src}"]`)) return
  const s = document.createElement('script')
  s.async = true
  s.src = src
  for (const [k, v] of Object.entries(attrs)) s.setAttribute(k, v)
  document.head.appendChild(s)
}

/** Google's "Privacy & messaging" CMP looks for this frame to know the page is ready for its dialog. */
const signalGooglefcPresent = () => {
  if (document.querySelector('iframe[name="googlefcPresent"]')) return
  if (!document.body) {
    setTimeout(signalGooglefcPresent, 0)
    return
  }
  const iframe = document.createElement('iframe')
  iframe.style.cssText = 'width:0;height:0;border:none;z-index:-1000;left:-1000px;top:-1000px;display:none'
  iframe.name = 'googlefcPresent'
  document.body.appendChild(iframe)
}

/**
 * Loads Google's tags in the only order that is compliant: Consent Mode v2 defaults, then the certified CMP
 * (AdSense Privacy & messaging, which shows the EEA/UK/CH dialog and updates the consent state), then GA4 and
 * AdSense, both of which honour that state. Does nothing unless VITE_ADS=on.
 */
export function ConsentGate() {
  useEffect(() => {
    if (!ADS_ENABLED) return
    window.dataLayer = window.dataLayer || []
    if (!window.gtag) {
      // gtag.js reads `arguments` objects from the data layer, not arrays
      window.gtag = function gtag() {
        // biome-ignore lint/complexity/noArguments: required by gtag.js
        window.dataLayer?.push(arguments)
      }
    }
    // denied where the dialog asks first, until the visitor answers it; granted everywhere else, where the dialog
    // never shows, so nothing would ever grant it (analytics would count nobody there)
    window.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      region: ASK_FIRST,
      wait_for_update: 500,
    })
    window.gtag('consent', 'default', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted',
    })
    loadScript(`https://fundingchoicesmessages.google.com/i/${BRAND.adsensePublisherId}?ers=1`)
    signalGooglefcPresent()
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${BRAND.ga4MeasurementId}`)
    window.gtag('js', new Date())
    window.gtag('config', BRAND.ga4MeasurementId, { anonymize_ip: true })
    loadScript(
      `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-${BRAND.adsensePublisherId}`,
      { crossorigin: 'anonymous' },
    )
  }, [])
  return null
}
